/* "How it works" on the landing page: the whole journey in seven steps, from a tap on the globe to the first weeks
   abroad. Each step says what the student does and what PathsAbroad does, and the app plays that step on a phone (the
   extension's step plays in a browser). On wide screens one phone stays beside the steps and changes with the step
   being read, and the route down the side fills in as you go; on phones each step carries its own screen.
   The screens are drawn in HTML and CSS like the page's other examples, carry the "Example" tag and are hidden from
   screen readers: the words say the same. They play once they're on screen, and not at all with reduced motion.
   Every claim is something the product does today, and the guide's lines are the planner's own. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { WEB_ROUTES } from '@pathabroad/core/browser';
import { useT } from '../../app/i18n';
import { Icon } from '../../app/icons';
import { cx } from '../../app/ui';
import { localeOf } from '../../lib/format';
import { DemoApply, DemoChecklist, DemoInbox, MiniMark, SCHOOLS, SchoolRow, Tag, Uni } from './Demos';
import { COUNTRIES, UNIVERSITIES } from './facts';
import { GLOBE_SIZE } from './globe';
import globeUrl from './globe.svg';
import { DEMO_HREF } from './GlobeHero';

type StepId = 's1' | 's2' | 's3' | 's4' | 's5' | 's6' | 's7';
type Step = { id: StepId; Screen: () => ReactNode; browser?: boolean };

/* the example student, Amina, is from Amman; on the globe (globe.svg's own coordinates) her three places */
const HOME: [number, number] = [527.6, 243.6];
const PICKS: Array<{ name: string; at: [number, number] }> = [
  { name: 'Toronto', at: [172.8, 222.9] },
  { name: 'Manchester', at: [394.3, 204] },
  { name: 'Munich', at: [438.7, 214] },
];
/* a raised route between two points on the globe's picture */
function arc([x1, y1]: [number, number], [x2, y2]: [number, number]) {
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2, lift = Math.hypot(x2 - x1, y2 - y1) * .32;
  return `M${x1} ${y1}Q${mx} ${my - lift} ${x2} ${y2}`;
}
const UNI_TORONTO = 'University of Toronto';

const STEPS: Step[] = [
  { id: 's1', Screen: Places },
  { id: 's2', Screen: Questions },
  { id: 's3', Screen: Matches },
  { id: 's4', Screen: Plan },
  { id: 's5', Screen: Apply, browser: true },
  { id: 's6', Screen: Inbox },
  { id: 's7', Screen: Go },
];

/* wide screens keep one phone beside the steps */
const WIDE = '(min-width: 1000px)';
function useWide() {
  const [wide, setWide] = useState(() => typeof matchMedia === 'function' && matchMedia(WIDE).matches);
  useEffect(() => {
    const m = matchMedia(WIDE), on = () => setWide(m.matches);
    on(); m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

/* true once the element has been on screen (it plays once) */
function useSeen<T extends Element>(threshold: number) {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { setSeen(true); io.disconnect(); } }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen] as const;
}

export function HowItWorks() {
  const { t, lang } = useT();
  const wide = useWide();
  const [active, setActive] = useState(0);
  const [ready, setReady] = useState(false);
  const items = useRef<Array<HTMLLIElement | null>>([]);
  const n = (v: number) => new Intl.NumberFormat(localeOf(lang)).format(v);

  /* the step being read: the one crossing the middle of the window */
  useEffect(() => {
    setReady(true);
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(es => {
      for (const e of es) if (e.isIntersecting) setActive(Number((e.target as HTMLElement).dataset.step));
    }, { rootMargin: '-46% 0px -53% 0px' });
    for (const el of items.current) if (el) io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section id="how" className={cx('land-section how', ready && 'how--ready')} aria-labelledby="how-title">
      <div className="land-wrap">
        <header className="land-head">
          <p className="land-eyebrow">{t('land.nav.how')}</p>
          <h2 id="how-title" className="land-h2">{t('land.how.title')}</h2>
          <p className="land-sub">{t('land.how.lead')}</p>
        </header>
        <div className="how-grid">
          <ol className="how-steps">
            {STEPS.map((s, i) => (
              <li key={s.id} ref={el => { items.current[i] = el; }} data-step={i}
                className={cx('how-step', i < active && 'is-done', i === active && 'is-now')}>
                <span className="how-step__node" aria-hidden="true">
                  {i < active ? <Icon.check /> : i === active ? <Icon.plane className="how-step__plane" /> : n(i + 1)}
                </span>
                <div className="how-step__body">
                  <p className="how-step__meta">
                    <span>{t('land.how.step', { n: n(i + 1), total: n(STEPS.length) })}</span>
                    <span className="how-time"><Icon.clock />{t(`land.how.${s.id}.time`)}</span>
                  </p>
                  <h3 className="land-h3 how-step__title">{t(`land.how.${s.id}.title`)}</h3>
                  <p className="how-step__text">{t(`land.how.${s.id}.text`, { unis: n(UNIVERSITIES), countries: n(COUNTRIES) })}</p>
                  <dl className="how-duo">
                    <div className="how-duo__row">
                      <dt><span className="how-duo__you" aria-hidden="true"><Icon.person /></span>{t('land.how.you')}</dt>
                      <dd>{t(`land.how.${s.id}.you`)}</dd>
                    </div>
                    <div className="how-duo__row how-duo__row--pa">
                      <dt><MiniMark /><bdi lang="en">PathsAbroad</bdi></dt>
                      <dd>{t(`land.how.${s.id}.pa`)}</dd>
                    </div>
                  </dl>
                </div>
                {!wide && <Inline step={s} />}
              </li>
            ))}
          </ol>
          {wide && <Stage active={active} />}
        </div>
        <div className="land-cta land-cta--center how-cta">
          <a href={WEB_ROUTES.planner} className="btn btn--primary land-btn">{t('land.cta.free')}</a>
          <a href={DEMO_HREF} className="btn land-btn land-btn--quiet"><Icon.play />{t('land.globe.demo')}</a>
        </div>
      </div>
    </section>
  );
}

/* wide screens: one stage that stays put, showing the step being read */
function Stage({ active }: { active: number }) {
  const [ref, seen] = useSeen<HTMLDivElement>(.35);
  return (
    <div className="how-stage-col">
      <div ref={ref} className="how-stage night" aria-hidden="true">
        <span className="how-stage__tag"><Tag /></span>
        {STEPS.map((s, i) => (
          <div key={s.id} className={cx('how-screen', s.browser && 'how-screen--browser', i === active && 'is-shown', seen && i === active && 'is-on')}>
            <s.Screen />
          </div>
        ))}
        <span className="how-stage__dots">{STEPS.map((s, i) => <i key={s.id} className={i === active ? 'is-on' : undefined} />)}</span>
      </div>
    </div>
  );
}

/* phones: each step's own screen, which plays once it's on screen */
function Inline({ step }: { step: Step }) {
  const [ref, seen] = useSeen<HTMLDivElement>(.4);
  return (
    <div ref={ref} className={cx('how-inline night', step.browser && 'how-inline--browser')} aria-hidden="true">
      <span className="how-stage__tag"><Tag /></span>
      <div className={cx('how-screen is-shown', step.browser && 'how-screen--browser', seen && 'is-on')}><step.Screen /></div>
    </div>
  );
}

/* ---------- the phone ---------- */
function Phone({ night, tab, children }: { night?: boolean; tab?: 'home' | 'explore' | 'apply'; children: ReactNode }) {
  const tabs: Array<[string, () => ReactNode]> = [['home', Icon.home], ['explore', Icon.globe], ['apply', Icon.list], ['more', Icon.more]];
  return (
    <div className="how-phone">
      <div className={cx('how-phone__screen', night ? 'how-phone__screen--night' : 'how-day')}>
        <div className="how-phone__status" dir="ltr"><span>9:41</span><span className="how-phone__island" /><span className="how-phone__bars"><i /><i /><i /><b /></span></div>
        <div className="how-phone__body">{children}</div>
        {tab && <div className="how-tabs">{tabs.map(([k, TabIcon]) => <span key={k} className={k === tab ? 'is-on' : undefined}><TabIcon /></span>)}</div>}
      </div>
    </div>
  );
}

/* 1. the globe: three places picked, routes from home, and the search */
function Places() {
  const { t, lang } = useT();
  const n = (v: number) => new Intl.NumberFormat(localeOf(lang)).format(v);
  return (
    <Phone night>
      <div className="hs-globe" dir="ltr">
        <img src={globeUrl} alt="" width={GLOBE_SIZE} height={GLOBE_SIZE} decoding="async" />
        <svg viewBox={`0 0 ${GLOBE_SIZE} ${GLOBE_SIZE}`} focusable="false">
          {PICKS.map((p, i) => <path key={p.name} className={`hs-arc hs-a hs-d${i + 5}`} d={arc(HOME, p.at)} pathLength={1} />)}
          <circle className="hs-home" cx={HOME[0]} cy={HOME[1]} r={8} />
          {PICKS.map((p, i) => (
            <g key={p.name} className={`hs-pin hs-a hs-pop hs-d${i + 2}`}>
              <circle className="hs-pin__ring" cx={p.at[0]} cy={p.at[1]} r={10} />
              <circle className="hs-pin__dot" cx={p.at[0]} cy={p.at[1]} r={7} />
            </g>
          ))}
        </svg>
      </div>
      <div className="hs-sheet">
        <p className="hs-eye"><Icon.globe />{t('land.facts.unis', { unis: n(UNIVERSITIES), countries: n(COUNTRIES) })}</p>
        <p className="hs-title">{t('land.how.d.where')}</p>
        <p className="hs-search"><Icon.search /><bdi lang="en" className="hs-typed">Toronto</bdi><span className="hs-caret" /></p>
        <p className="hs-chips">
          {PICKS.map((p, i) => <span key={p.name} className={`hs-chip hs-a hs-pop hs-d${i + 2}`}><bdi lang="en">{p.name}</bdi><Icon.close /></span>)}
        </p>
        <span className="hs-cta hs-a hs-d6">{t('land.how.d.continue', { n: n(PICKS.length) })}</span>
      </div>
    </Phone>
  );
}

/* 2. the guide's questions over the dimmed globe */
function Questions() {
  const { t } = useT();
  return (
    <Phone night>
      <div className="hs-dim" dir="ltr"><img src={globeUrl} alt="" width={GLOBE_SIZE} height={GLOBE_SIZE} decoding="async" /></div>
      <div className="hs-chat">
        <p className="hs-chat__head"><MiniMark /><span>{t('land.how.d.guide')}</span><span className="chip chip--ai">{t('ai.tag')}</span></p>
        <p className="hs-bot hs-a hs-d1">{t('land.how.d.career')}</p>
        <p className="hs-me hs-a hs-d3">{t('land.how.d.civil')}</p>
        <p className="hs-bot hs-a hs-d5">{t('land.how.d.level')}</p>
        <p className="hs-me hs-a hs-d7">{t('land.how.d.yes')}</p>
        <p className="hs-finding hs-a hs-d8"><span className="hs-spin" />{t('land.how.d.finding')}</p>
      </div>
    </Phone>
  );
}

/* 3. the matches, with chances and the scholarships that fit */
function Matches() {
  const { t } = useT();
  return (
    <Phone tab="explore">
      <p className="hs-head"><Icon.map />{t('land.demo.matches')}</p>
      <div className="mock-filters">
        <span className="mock-filter mock-filter--on">{t('land.demo.all')}</span>
        <span className="mock-filter">{t('land.demo.likely')}</span>
        <span className="mock-filter">{t('land.demo.fit')}</span>
        <span className="mock-filter">{t('land.demo.ambitious')}</span>
      </div>
      <ul className="mock-list hs-rows">{SCHOOLS.slice(0, 3).map(s => <SchoolRow key={s.name} s={s} meter />)}</ul>
      <p className="hs-scholar hs-a hs-d6"><Icon.ok />{t('land.how.d.scholar')}</p>
      <p className="hs-foot">{t('land.demo.estimate')}</p>
      <span className="hs-save hs-a hs-d7">{t('land.how.d.save')}</span>
    </Phone>
  );
}

/* 4. Home: the guide's next step, the step itself, and the dates coming up */
function Plan() {
  const { t, rich, lang } = useT();
  const loc = localeOf(lang);
  const day = (k: number) => {
    const d = new Date(Date.now() + k * 864e5);
    return [new Intl.DateTimeFormat(loc, { month: 'short' }).format(d), new Intl.DateTimeFormat(loc, { day: 'numeric' }).format(d)];
  };
  const rows: Array<[number, string]> = [[12, UNI_TORONTO], [16, 'The University of Manchester'], [23, 'Technical University of Munich']];
  return (
    <Phone tab="home">
      <p className="hs-guidebar hs-a hs-d1">
        <span className="hs-guidebar__mark"><Icon.spark /></span>
        <span className="hs-guidebar__text"><b>{t('land.how.d.guide')}</b>{t('land.how.d.next')}</span>
        <span className="hs-guidebar__ask">{t('land.how.d.ask')}</span>
      </p>
      <div className="hs-next night hs-a hs-d2">
        <p className="hs-next__eye">{t('phase.summary', { n: 2, name: t('phase.1') })}</p>
        <p className="hs-next__title">{t('land.how.d.task')}</p>
        <p className="hs-next__sub"><Uni>{UNI_TORONTO}</Uni> · {t('land.how.d.due')}</p>
        <span className="hs-next__btn">{t('land.how.d.doIt')}</span>
      </div>
      <p className="hs-h">{t('land.how.d.coming')}</p>
      <ul className="hs-dates">
        {rows.map(([k, uni], i) => {
          const [m, d] = day(k);
          return (
            <li key={uni} className={`hs-a hs-d${i + 4}`}>
              <span className="hs-date"><small>{m}</small><b>{d}</b></span>
              <span className="hs-dates__main"><b>{rich('land.how.d.deadline', { school: <Uni>{uni}</Uni> })}</b><small>{t('land.how.d.inDays', { n: k })}</small></span>
            </li>
          );
        })}
      </ul>
    </Phone>
  );
}

/* 5. the extension filling a portal's form (the example the page's Apply feature shows), playing */
function Apply() {
  return <div className="how-browser"><DemoApply /></div>;
}

/* 6. the application inbox sorting the news, and a WhatsApp reminder coming in */
function Inbox() {
  const { t, rich } = useT();
  return (
    <Phone tab="apply">
      <div className="hs-notif hs-a hs-d7">
        <p className="hs-notif__head"><span className="mock-wa__dot" />{t('land.demo.whatsapp')}<span className="mock__time">09:00</span></p>
        <p>{rich('land.demo.reminder', { school: <Uni>{UNI_TORONTO}</Uni> })}</p>
      </div>
      <div className="hs-inbox"><DemoInbox /></div>
    </Phone>
  );
}

/* 7. ready to go: the trip, and the visa checklist ticking off */
function Go() {
  const { t } = useT();
  return (
    <Phone tab="apply">
      <div className="hs-pass night hs-a hs-d1">
        <p className="hs-pass__eye"><Icon.plane />{t('land.how.d.trip')}</p>
        <div className="hs-pass__route">
          <span className="hs-pass__code"><b dir="ltr">AMM</b><small lang="en">Amman</small></span>
          <span className="hs-pass__line"><span className="hs-pass__plane"><Icon.plane /></span></span>
          <span className="hs-pass__code hs-pass__code--to"><b dir="ltr">YYZ</b><small lang="en">Toronto</small></span>
        </div>
        <p className="hs-pass__uni"><Uni>{UNI_TORONTO}</Uni><span>{t('land.how.d.starts')}</span></p>
      </div>
      <p className="hs-h">{t('land.how.d.visa')}</p>
      <div className="hs-check"><DemoChecklist /></div>
    </Phone>
  );
}
